import { NgComponentOutlet } from '@angular/common';
import {
  EventEmitter,
  Injector,
  OutputEmitterRef,
  SimpleChanges,
  Type,
  Directive,
  Input,
  input,
} from '@angular/core';
import { outputToObservable } from '@angular/core/rxjs-interop';

import type { Subscription } from 'rxjs';
import { merge, tap, isObservable } from 'rxjs';

@Directive({
  selector: '[rtComponentOutlet]',
  standalone: true,
})
export class RtComponentOutlet<T extends Type<any>> extends NgComponentOutlet<T> {
  public readonly rtComponentOutletOutputs = input<
    Partial<{
      [key in keyof InstanceType<T> as InstanceType<T>[key] extends
        | EventEmitter<any>
        | OutputEmitterRef<any>
        ? key
        : never]: (
        arg: InstanceType<T>[key] extends EventEmitter<infer EE>
          ? EE
          : InstanceType<T>[key] extends OutputEmitterRef<infer OER>
            ? OER
            : never,
      ) => void;
    }>
  >();

  @Input({
    alias: 'rtComponentOutlet',
  })
  public ngComponentOutlet: T | null = null;
  @Input({
    alias: 'rtComponentOutletInputs',
  })
  public ngComponentOutletInputs?: Partial<Record<keyof InstanceType<T>, unknown>>;
  @Input({
    alias: 'rtComponentOutletInjector',
  })
  public ngComponentOutletInjector?: Injector;
  @Input({
    alias: 'rtComponentOutletContent',
  })
  public ngComponentOutletContent?: any[][];
  @Input({
    alias: 'rtComponentOutletNgModuleFactory',
  })
  public ngComponentOutletNgModule?: Type<any>;

  private outputsSub: Subscription | null = null;

  public ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);

    if (changes.rtComponentOutletOutputs || changes.rtComponentOutlet) {
      const outputs = this.rtComponentOutletOutputs() || {};
      this.outputsSub?.unsubscribe();
      const componentRef = this.componentInstance;
      if (componentRef) {
        this.outputsSub = merge(
          ...Object.entries(outputs).map(([key, handler]) => {
            const observableOrOutputRef: EventEmitter<any> | OutputEmitterRef<any> =
              this['_componentRef']!.instance[key];
            const observable$ = isObservable(observableOrOutputRef)
              ? observableOrOutputRef
              : outputToObservable(observableOrOutputRef);
            return observable$.pipe(tap(handler!));
          }),
        ).subscribe();
      }
    }
  }

  public ngOnDestroy() {
    super.ngOnDestroy();
    this.outputsSub?.unsubscribe();
  }
}
